import _ from 'lodash'
import { ASTKinds, type Expression, type Modifier, parse } from './dice'
import { type Die, roll } from './diceRoll'
import diceFunctions from './diceFunctions'

type DiceExpression = Expression

export type DiceExpr = string | number

export type ExtraMod = {
  type: string
  arg?: number
}

export type DiceContext = {
  variables: Record<string, DiceExpr>
  globalMods: Modifier[]
  extraMods: ExtraMod[]
}

export type NumberPart = { type: 'num'; num: number }
export type OperatorPart = { type: 'op'; op: string }
export type VariablePart = { type: 'var'; value: number; name: string }
export type DicePart = { type: 'die'; die: Die }
export type TextPart = { type: 'text'; text: string }
export type ResultPart = OperatorPart | DicePart | VariablePart | NumberPart | TextPart
export type SingleDiceResult = {
  rolled: number
  average: number
  result: ResultPart[]
  resultSimple: ResultPart[]
  constant: boolean
  expression: string
  simplified: string
}

export type MultiDiceResult = {
  results: SingleDiceResult[]
  player: string
  id: number
  hidden?: boolean
  label: string | null
}

function join(lhs: DiceExpr, rhs: DiceExpr, op = '+'): DiceExpr | null {
  const split = (expr: DiceExpr) => {
    expr = `${expr}`
    const rx = /(.*)\s*(?:!(.*))?/
    const res = rx.exec(expr)
    if (res === null) return []
    return res.slice(1)
  }

  const [lhsExpr = '', lhsMods = ''] = split(lhs)
  // eslint-disable-next-line prefer-const
  let [rhsExpr = '', rhsMods = ''] = split(rhs)

  if (!lhsExpr && !rhsExpr) return null

  if (rhsExpr.startsWith('-') && op === '+') {
    op = '-'
    rhsExpr = rhsExpr.slice(1)
  } else if (rhsExpr.startsWith('+')) {
    rhsExpr = rhsExpr.slice(1)
  }

  const expr = `${lhsExpr}${op}${rhsExpr}`
  const mods = lhsMods || rhsMods ? ` !${lhsMods}${rhsMods}` : ''

  return `${expr}${mods}`
}

export function joinDice(...exprs: DiceExpr[]): DiceExpr {
  return exprs.filter((x) => !!x).reduce((lhs, rhs) => join(lhs, rhs) ?? '')
}

export function isCheck(res: SingleDiceResult): boolean {
  // a single d20 is part of the result => is a check
  const dice = res.result.filter((d) => d.type === 'die' && d.die.sides === 20 && !d.die.tags.includes('ignored'))
  return dice.length === 1
}

export function isCriticalCheck(res: SingleDiceResult): boolean {
  // a single d20 is part of the result and it rolled maximum => is a crit
  const dice = res.result.filter(
    (d) => d.type === 'die' && d.die.sides === 20 && d.die.tags.includes('maximum') && !d.die.tags.includes('ignored'),
  )
  return dice.length === 1
}

export function resolveDice(expr: DiceExpr, variables: Record<string, number> = {}, constant: boolean = false): number {
  const ctx = { variables, globalMods: [], extraMods: [] }
  const diceExpr = parseDice(expr, ctx)
  if (!diceExpr) return 0
  const result = evaluate(diceExpr, ctx)
  return constant ? result.average : result.rolled
}

export function parseDice(expr: DiceExpr, ctx?: DiceContext): DiceExpression | null {
  expr = `${expr}` // force string
  const input = expr.replaceAll(/\s/g, '')
  const result = parse(input)
  if (result.errs.length) console.error(`dice parsing error: '${expr}'`, result.errs)
  if (ctx) ctx.globalMods = result.ast?.globalMods?.modifiers ?? []
  return result.ast?.expression ?? null
}

// errors and identity constants can be ignored
const canIgnore = (expr: SingleDiceResult, identity: number) =>
  expr === ERROR || (expr.constant && expr.average === identity && expr.result.length === 1)

function binary(
  lhs: SingleDiceResult,
  rhs: SingleDiceResult,
  fn: (a: number, b: number) => number,
  identity: number,
  op: string,
  opNice: string = op,
): SingleDiceResult {
  if (canIgnore(lhs, identity)) return rhs
  if (canIgnore(rhs, identity)) return lhs
  const rolled = fn(lhs.rolled, rhs.rolled)
  const average = fn(lhs.average, rhs.average)
  const constant = lhs.constant && rhs.constant
  const result: ResultPart[] = [...lhs.result, { type: 'op', op }, ...rhs.result]
  const resultSimple: ResultPart[] = constant
    ? ([{ type: 'num', num: average }] as NumberPart[])
    : [...lhs.resultSimple, { type: 'op', op } as OperatorPart, ...rhs.resultSimple]
  const expression = `${lhs.expression}${opNice}${rhs.expression}`
  const simplified = constant ? `${average}` : `${lhs.simplified}${opNice}${rhs.simplified}`
  const res: SingleDiceResult = {
    rolled,
    average,
    result,
    resultSimple,
    constant,
    expression,
    simplified,
  }
  return res
}

const ERROR: SingleDiceResult = {
  rolled: 0,
  average: 0,
  result: [{ type: 'text', text: 'error' }],
  resultSimple: [{ type: 'text', text: 'error' }],
  constant: true,
  expression: `(error)`,
  simplified: `(error)`,
}

export function evaluate(expr: DiceExpression, ctx: DiceContext): SingleDiceResult {
  const e = (x: DiceExpression) => evaluate(x, ctx)
  const str = (x: any) => `${x}`
  if (!expr) return ERROR
  switch (expr.kind) {
    case ASTKinds.Dice: {
      const amount = expr.amount ? e(expr.amount).rolled : 1
      const sides = e(expr.sides).rolled

      return roll(amount, sides, expr.modifiers, ctx)
    }
    case ASTKinds.Constant: {
      const value = parseInt(expr.value)
      return {
        rolled: value,
        average: value,
        result: [{ type: 'num', num: value }],
        resultSimple: [{ type: 'num', num: value }],
        constant: true,
        expression: str(value),
        simplified: str(value),
      }
    }
    case ASTKinds.Variable: {
      const { name } = expr
      const variable = ctx.variables[name] || 0

      // variables can contain expressions, which is very handy when combined with toggles
      // We do not pass the ctx, since applying global mods to a half-evaluated expression is more confusing than useful
      const parsed = parseDice(variable)

      if (!parsed) return ERROR
      const value = e(parsed)

      if (value.constant) {
        value.result = [{ type: 'var', value: value.rolled, name }]
        value.resultSimple = [{ type: 'var', value: value.rolled, name }]
      }
      return value
    }
    case ASTKinds.Negate: {
      const res = e(expr.expr)
      return {
        rolled: -res.rolled,
        average: -res.average,
        result: [{ type: 'op', op: '-' }, ...res.result],
        resultSimple: [{ type: 'op', op: '-' }, ...res.resultSimple],
        constant: res.constant,
        expression: `-${res.expression}`,
        simplified: `-${res.simplified}`,
      }
    }
    case ASTKinds.Parens: {
      const res = e(expr.expr)
      return {
        rolled: res.rolled,
        average: res.average,
        result: [{ type: 'text', text: '(' }, ...res.result, { type: 'text', text: ')' }],
        resultSimple: [{ type: 'text', text: '(' }, ...res.result, { type: 'text', text: ')' }],
        constant: res.constant,
        expression: `(${res.expression})`,
        simplified: `(${res.simplified})`,
      }
    }
    case ASTKinds.Add: {
      const lhs = e(expr.lhs)
      const rhs = e(expr.rhs)
      return binary(lhs, rhs, (a, b) => a + b, 0, '+')
    }
    case ASTKinds.Multiply: {
      const lhs = e(expr.lhs)
      const rhs = e(expr.rhs)
      return binary(lhs, rhs, (a, b) => a * b, 1, '*', '×')
    }
    case ASTKinds.Divide: {
      const lhs = e(expr.lhs)
      const rhs = e(expr.rhs)
      return binary(lhs, rhs, (a, b) => Math.floor(a / b), 1, '/')
    }
    case ASTKinds.DivideUp: {
      const lhs = e(expr.lhs)
      const rhs = e(expr.rhs)
      return binary(lhs, rhs, (a, b) => Math.ceil(a / b), 1, '//')
    }
    case ASTKinds.Subtract: {
      const lhs = e(expr.lhs)
      const rhs = e(expr.rhs)
      return binary(lhs, rhs, (a, b) => a - b, 0, '-')
    }
    case ASTKinds.Function: {
      const funcName = expr.name.toLowerCase()
      const funcFn = diceFunctions[funcName]
      if (!funcFn) return ERROR

      const args = expr.args.map((arg) => e(arg.expr))
      return funcFn(args, ctx)
    }
  }
}
